import { Modal, Tabs }                   from "@mantine/core";
import dynamic                           from "next/dynamic";
import { Fragment, useEffect, useState } from "react";
import { useTranslation }                from "react-i18next";
import { Waypoint }                      from "react-waypoint";

import { storeEvent }                         from "_analytics/index";
import { useGetProfileId }                    from "_common/hooks/auth";
import { useGetDeviceType }                   from "_common/hooks/global";
import { shortenNumber }                      from "_common/utils";
import Icon                                   from "_components/atoms/icon";
import Loader                                 from "_components/atoms/loader";
import MobileBottomDrawer                     from "_components/atoms/mobileBottomDrawer";
import Typography                             from "_components/atoms/typography";
import UserView                               from "_components/molecules/searchResultUserView";
import { useGetModerators }                   from "_components/pages/studio/states";
import { getModeratorsCount }                 from "_components/pages/studio/utils";
import { getUserFollowers, getUserFollowing } from "_services/api/user";
import useStyles                              from "_styles/organisms/followInfoBox";

const ModeratorsTab = dynamic ( () => import ( "_components/features/chat/management/components/moderatorsTab" ), { ssr: false } );

const component = ( { activeType, userId, open, toggleDialogInfoBox, followers, following } ) => {
	const [ activeTab, setactiveTab ] = useState ( activeType );
	const moderatorsList              = useGetModerators ();
	const moderatorsCount             = getModeratorsCount ( moderatorsList );
	const ownProfileId                = useGetProfileId ();
	const isOwnProfile                = ( ownProfileId === userId );
	const { classes }                 = useStyles ( { isOwnProfile }, { name: "followInfoBox" } );

	const deviceType = useGetDeviceType ();
	const isMobile   = deviceType === "mobile";

			  // followers = followers.toString ().replace ( /\B(?=(\d{3})+(?!\d))/g, "," );
	followers = shortenNumber ( followers );
	following = shortenNumber ( following );

	const [ followerData, setFollowerData ]   = useState ( {
		pageNo     : 1,
		data       : [],
		isDepleted : false,
		isFetching : false
	} );
	const [ followingData, setFollowingData ] = useState ( {
		pageNo     : 1,
		data       : [],
		isDepleted : false,
		isFetching : false
	} );

	const { t } = useTranslation ();
			  // const darkMode = useGetDarkMode ();

	const handleTabChange = e => {
		storeEvent ( "Profile", "Click", e === "1" ? "Following" : "Followers" );
		setactiveTab ( e );
	};

	const fetchData = async currentType => {
		const params = {
			userId,
			pageNo   : currentType === 0 ? followerData.pageNo : followingData.pageNo,
			pageSize : 10
		};

		if ( currentType === 1 ) {
			setFollowingData ( {
				...followingData,
				isFetching: true
			} );
			const result = await getUserFollowing ( params );

			setFollowingData ( {
				isFetching : false,
				isDepleted : result.length ? false : true,
				data       : [ ...followingData.data, ...result ],
				pageNo     : followingData.pageNo + 1
			} );
		} else if ( currentType === 0 ) {
			setFollowerData ( {
				...followerData,
				isFetching: true
			} );
			const result = await getUserFollowers ( params );

			setFollowerData ( {
				isFetching : false,
				isDepleted : result.length ? false : true,
				data       : [ ...followerData.data, ...result ],
				pageNo     : followerData.pageNo + 1
			} );
		}
	};

	useEffect ( () => {
		if ( activeTab === "0" ) {
			if ( !followerData.data.length ) fetchData ( 0 );
		} else if ( activeTab === "1" ) {
			if ( !followingData.data.length ) fetchData ( 1 );
		}
	}, [ activeTab ] );

	useEffect ( () => {
		setactiveTab ( activeType );

	}, [ activeType ] );

	const loaderComponent = <Loader noMargin />;

	const content = (
		<>
			<div className = { classes.appBar }>
				<div className = { classes.crossIconContainer }
					onClick       = { toggleDialogInfoBox }
				>

					<Icon className = { classes.crossIcon }
						name           = "cross"
					/>

				</div>

				<div>
					<Tabs
						onChange = { handleTabChange }
						styles   = { {
							panel: {
								height : isMobile ? "50vh" : "calc( 100vh - 115px )",
								width  : "100%"
							}
						} }
						value    = { activeTab }
					>
						<Tabs.List>

							<Tabs.Tab
								value = { "0" }
							>
								<Typography
									className = { activeTab === "0" ? classes.activeText : classes.unActiveText }
									color     = { activeTab === "0" ? "primary" : "secondary" }
									title     = { `${ t ( "followinfo_followers" ) } (${ followers })` }
								/>

							</Tabs.Tab>

							<Tabs.Tab
								value = { "1" }
							>
								<Typography
									className = { activeTab === "1" ? classes.activeText : classes.unActiveText }
									color     = { activeTab === "1" ? "primary" : "secondary" }
									title     = { `${ t ( "followinfo_following" ) } (${ following })` }
								/>

							</Tabs.Tab>

							{isOwnProfile && (
								<Tabs.Tab
									value = { "2" }
								>
									<Typography
										className = { activeTab === "2" ? classes.activeText : classes.unActiveText }
										color     = { activeTab === "2" ? "primary" : "secondary" }
										title     = { `Moderators (${ moderatorsCount })` }
									/>

								</Tabs.Tab>
							)}
						</Tabs.List>
					</Tabs>
				</div>
			</div>

			{activeTab === "0" && (
				<div className = { classes.cardsListContainer }>

					{followerData.isDepleted && followerData.data.length === 0 && (
						<div className = { classes.emptyStateContainer }>
							<Typography className = "fontBold"
								title                = { "No followers" }
							/>
						</div>
					)}

					{followerData.data && followerData.data.map ( ( user, index ) => {
						const { isFollowed, ...others } = user;
						const formattedUser             = {
							...others,
							isFollowing: isFollowed
						};

						return (
							<Fragment key = { formattedUser.id }>
								<UserView
									source              = "User-Profile"
									sportsFanDetails    = { formattedUser }
									toggleDialogInfoBox = { toggleDialogInfoBox }
								/>

								<div className = { classes.separator }></div>

								{!followerData.isDepleted && !followerData.isFetching && index > followerData.data.length - 5 && <Waypoint onEnter = { () => fetchData ( 0 ) } />}
							</Fragment>
						);
					} )}

					{followerData.isFetching && loaderComponent}

				</div>
			)}

			{activeTab === "1" && (
				<div className = { classes.cardsListContainer }>

					{/* {followingData.isFetching && !followingData.data.length && loaderComponent} */}

					{followingData.isDepleted && followingData.data.length === 0 && (
						<div className = { classes.emptyStateContainer }>
							<Typography className = "fontBold"
								title                = "No Following"
							/>
						</div>
					)}

					{followingData && followingData.data.map ( ( user, index ) => {
						const { isFollowed, ...others } = user;
						const formattedUser             = {
							...others,
							isFollowing: isFollowed
						};

						return (
							<Fragment key = { formattedUser.id }>
								<UserView
									source              = "User-Profile"
									sportsFanDetails    = { formattedUser }
									toggleDialogInfoBox = { toggleDialogInfoBox }
								/>

								<div className = { classes.separator }></div>

								{!followingData.isDepleted && !followingData.isFetching && index > followingData.data.length - 5 && <Waypoint onEnter = { () => fetchData ( 1 ) } />}
							</Fragment>
						);
					} )}

					{followingData.isFetching && loaderComponent}

				</div>
			)}

			{
				( isOwnProfile && activeTab === "2" ) && (
					<ModeratorsTab
						isInUserProfile
					/>
				)
			}
		</>
	);

	if ( isMobile ) {
		return (
			<MobileBottomDrawer
				noBorderRadius
				isOpen  = { open }
				onClose = { toggleDialogInfoBox }
				size    = "100%"
				styles  = { { body: { display: "flex", flexDirection: "column" } } }
			>
				{content}
			</MobileBottomDrawer>
		);
	}

	return (
		<Modal
			centered
			classNames      = { {
				body    : classes.dialogRoot,
				content : classes.modalContent
			} }
			closeOnEscape   = { true }
			onClose         = { toggleDialogInfoBox }
			opened          = { open }
			radius          = "lg"
			transitionProps = { { transition: "slide-up", duration: 200, timingFunction: "ease" } }
			withCloseButton = { false }
			zIndex          = { 2000 }
		>
			{content}
		</Modal>
	);
};

export default component;
